// Pending GitHub Actions failure triggers — sibling of PendingCommentTriggers
// for the address-checks pipeline. Same UX (cancel / dispatch-now) with two
// differences: the entry says "X failed workflows" instead of comments, and
// the "Dispatches" column reflects a SOFT eta — the scheduler will still
// wait for all checks on the PR to settle before firing (re-queried from
// GitHub on every tick), so this is "earliest the scheduler will consider".

import { useCallback, useState } from "react";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import { apiErrorMessage, relativeTime } from "./shared";

const POLL_INTERVAL = 15000;

function readyCountdown(readyAtIso, now) {
  const ms = new Date(readyAtIso).getTime() - now;
  if (ms <= 0) return "ready when checks settle";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `in ${m}m ${s}s` : `in ${s}s`;
}

export default function PendingCheckTriggers({ onChange }) {
  const fetchTriggers = useCallback(
    () => api.bssCheckTriggers().then((res) => res.data),
    []
  );
  const { data, error, refresh } = useBssPoll(fetchTriggers, {
    interval: POLL_INTERVAL,
    shouldContinue: () => true
  });
  const [busyId, setBusyId] = useState(null);

  const items = (data && data.items) || [];
  const now = Date.now();

  const cancel = async (id) => {
    setBusyId(id);
    try {
      await api.bssCancelCheckTrigger(id);
      refresh();
      onChange && onChange();
    } finally {
      setBusyId(null);
    }
  };

  const dispatchNow = async (id) => {
    setBusyId(id);
    try {
      await api.bssDispatchCheckTriggerNow(id);
      refresh();
      onChange && onChange();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="bss-card"
      style={{ marginBottom: 12, padding: "8px 12px" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
          fontSize: 13,
          fontWeight: 600,
          color: "var(--bss-danger)"
        }}
      >
        <Icon.AlertCircle size={14} />
        Pending CI-fix triggers ({items.length})
        <span
          style={{ fontWeight: 400, color: "var(--bss-text-dim)", marginLeft: 4 }}
        >
          — waiting for all checks on each PR to settle before dispatching
        </span>
      </div>

      {error && (
        <div style={{ color: "var(--bss-danger)", fontSize: 12 }}>
          {apiErrorMessage(error)}
        </div>
      )}

      {items.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--bss-text-dim)",
            padding: "4px 2px"
          }}
        >
          No pending triggers — when a workflow fails on an agent PR, a row
          will appear here while the rest of the suite finishes.
        </div>
      ) : (
        <table className="bss-table" style={{ marginTop: 0 }}>
          <thead>
            <tr>
              <th style={{ width: 110 }}>Issue</th>
              <th style={{ width: 90 }}>PR</th>
              <th>Branch</th>
              <th style={{ width: 100, textAlign: "right" }}>Failures</th>
              <th style={{ width: 140 }}>Last failure</th>
              <th style={{ width: 180 }}>Eligible</th>
              <th style={{ width: 200 }} aria-label="actions" />
            </tr>
          </thead>
          <tbody>
            {items.map((t) => {
              const busy = busyId === t.id;
              return (
                <tr key={t.id}>
                  <td>{t.issueKey}</td>
                  <td>#{t.prNumber}</td>
                  <td
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 320
                    }}
                    title={t.branchName}
                  >
                    {t.branchName}
                  </td>
                  <td style={{ textAlign: "right" }}>{t.failureCount}</td>
                  <td>{relativeTime(t.lastFailureAt)}</td>
                  <td>{readyCountdown(t.readyAt, now)}</td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className="bss-btn bss-btn-sm"
                      style={{ marginRight: 6 }}
                      disabled={busy}
                      onClick={() => dispatchNow(t.id)}
                      title="Make eligible immediately — settled-checks gate still applies"
                    >
                      Dispatch now
                    </button>
                    <button
                      type="button"
                      className="bss-btn bss-btn-sm bss-btn-danger"
                      disabled={busy}
                      onClick={() => cancel(t.id)}
                      title="Cancel — the row is removed; future failures will create a fresh trigger"
                    >
                      Cancel
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
